import React, { useState, useEffect } from 'react';
import './landing.css';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';

function C2wLanding({ onCompaniesClick, onPlacementsClick, onIncubatorsClick }) {
  const navigate = useNavigate();
  const [countData, setCountData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch('/website.json');
        if (res.ok) {
          const json = await res.json();
          setCountData(json);
        }
      } catch (err) {
        console.error('Failed to load JSON stats', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Format stats counts from website.json dynamic data
  const studentCount = countData?.studentCount
    ? `${Math.floor(parseInt(countData.studentCount.replace(/,/g, '')) / 1000)}K+`
    : '40K+';
  const placementCount = countData?.placementCount
    ? `${Math.floor(parseInt(countData.placementCount.replace(/,/g, '')) / 1000)}K+`
    : '30K+';
  const companiesCount = countData?.companiesCount
    ? `${countData.companiesCount}+`
    : '170+';

  // Language columns for vertical marquee
  const col1Icons = [
    { src: '/assets/images/landing/pythonLogoSvg.svg', alt: 'Python' },
    { src: '/assets/images/landing/springLogoSvg.svg', alt: 'Spring Boot' },
    { src: '/assets/images/landing/dbmsLogoSvg.svg', alt: 'MySQL' },
    { src: '/assets/images/landing/javaLogoSvg.svg', alt: 'Java' },
    { src: '/assets/images/landing/cLogoSvg.svg', alt: 'C' },
    { src: '/assets/images/landing/dsaLogoSvg.svg', alt: 'DSA' },
    { src: '/assets/images/landing/iotLogoSvg.svg', alt: 'IoT' }
  ];

  const col2Icons = [
    { src: '/assets/images/landing/flutterSvg.svg', alt: 'Flutter' },
    { src: '/assets/images/landing/cppLogoSvg.svg', alt: 'C++' },
    { src: '/assets/images/landing/dbmsLogoSvg.svg', alt: 'MySQL' },
    { src: '/assets/images/landing/cLogoSvg.svg', alt: 'C' },
    { src: '/assets/images/landing/reactLogoSvg.svg', alt: 'React' },
    { src: '/assets/images/landing/jsSvg.svg', alt: 'JavaScript' },
    { src: '/assets/images/landing/iotLogoSvg.svg', alt: 'IoT' }
  ];

  const isDesktop = windowWidth > 1024;

  // Animation variants
  const badgeVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const titleContainerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const titleRowVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
  };

  const sirVariants = {
    hidden: { opacity: 0, scale: 0.96, y: 50 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 1.0, ease: [0.25, 1, 0.5, 1], delay: 0.15 } }
  };

  const starsVariants = {
    hidden: { opacity: 0, scale: 0.8, x: -30 },
    visible: { opacity: 1, scale: 1, x: 0, transition: { type: "spring", stiffness: 100, damping: 15, delay: 0.85 } }
  };

  const nameVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 25 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 15, delay: 1.0 } }
  };

  const rightColVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut", delay: 0.55 } }
  };

  const statsVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1], delay: 0.75 } }
  };

  const cardsContainerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.95 } }
  };

  const cardItemVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] } }
  };

  return (
    <div className="C2WLanding_ctn">
      {/* Background radial glow indicators */}
      <div className="landing_glow glow_orange"></div>
      <div className="landing_glow glow_blue"></div>

      {/* Hero content fold */}
      <div className="C2wLanding_main_fold">
        {/* Left Column: Heading & Badges */}
        <div className="C2wLanding_left_col">
          <motion.div 
            className="exposure-badge"
            variants={badgeVariants}
            initial="hidden"
            animate="visible"
          >
            <span className="bullet-light">💡</span>
            <span>Real-World Exposure That Builds Industry-Ready Skills.</span>
          </motion.div>

          <motion.h1 
            className="hero-main-title"
            variants={titleContainerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.span className="title-row" variants={titleRowVariants}>IT Training</motion.span>
            <motion.span className="title-row gradient-text" variants={titleRowVariants}>Programming Expertise</motion.span>
            <motion.span className="title-row" variants={titleRowVariants}>Hands-On Projects</motion.span>
            <motion.span className="title-row" variants={titleRowVariants}>Entrepreneurship</motion.span>
          </motion.h1>
        </div>

        {/* Center Column: Shashi Sir Photo with Overlays */}
        <div className="C2wLanding_center_col">
          <motion.div 
            className="teacher-experience-overlay"
            variants={starsVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="stars">★★★★★</div>
            <div className="years-text">10+ Years</div>
            <div className="subtitle">of experience in teaching</div>
          </motion.div>

          <motion.div 
            className="sir-image-wrapper"
            variants={sirVariants}
            initial="hidden"
            animate="visible"
          >
            <img
              src="/assets/images/landing/sirImg.png"
              alt="Shashi Bagal"
              className="sir-image"
            />
          </motion.div>

          <motion.a
            href="https://www.linkedin.com/in/shashi-bagal-36b392b7/"
            target="_blank"
            rel="noopener noreferrer"
            className="teacher-name-overlay"
            variants={nameVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="name-info">
              <span className="name">Shashi Bagal</span>
              <span className="role">Master</span>
            </div>
            <div className="linkedin-icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" fill="#0A66C2"/>
              </svg>
            </div>
          </motion.a>
        </div>

        {/* Right Column: Double Infinite Vertical Marquees */}
        {isDesktop && (
          <motion.div 
            className="C2wLanding_right_col"
            variants={rightColVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Column 1: Upward scrolling loop */}
            <div className="marquee-wrapper wrapper-up">
              <div className="marquee-track track-up">
                {col1Icons.map((icon, idx) => (
                  <div className="marquee-item" key={`up1-${idx}`}>
                    <img src={icon.src} alt={icon.alt} />
                  </div>
                ))}
                {col1Icons.map((icon, idx) => (
                  <div className="marquee-item" key={`up2-${idx}`}>
                    <img src={icon.src} alt={icon.alt} />
                  </div>
                ))}
              </div>
            </div>

            {/* Column 2: Downward scrolling loop */}
            <div className="marquee-wrapper wrapper-down">
              <div className="marquee-track track-down">
                {col2Icons.map((icon, idx) => (
                  <div className="marquee-item" key={`down1-${idx}`}>
                    <img src={icon.src} alt={icon.alt} />
                  </div>
                ))}
                {col2Icons.map((icon, idx) => (
                  <div className="marquee-item" key={`down2-${idx}`}>
                    <img src={icon.src} alt={icon.alt} />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Horizontal Marquee for Mobile/Tablet views */}
      {!isDesktop && (
        <motion.div 
          className="C2wLanding_mobile_marquee"
          variants={rightColVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="mobile-marquee-track">
            {[...col1Icons, ...col2Icons].map((icon, idx) => (
              <div className="marquee-item" key={`mob1-${idx}`}>
                <img src={icon.src} alt={icon.alt} />
              </div>
            ))}
            {[...col1Icons, ...col2Icons].map((icon, idx) => (
              <div className="marquee-item" key={`mob2-${idx}`}>
                <img src={icon.src} alt={icon.alt} />
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Stats Bar */}
      <motion.div 
        className="C2wLanding_stats_bar"
        variants={statsVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="stat-box">
          <span className="value">{studentCount}</span>
          <span className="label">Students</span>
        </div>
        <div className="divider"></div>
        <div className="stat-box clickable" onClick={onPlacementsClick}>
          <span className="value">{placementCount}</span>
          <span className="label">Placements</span>
        </div>
        <div className="divider"></div>
        <div className="stat-box clickable" onClick={onCompaniesClick}>
          <span className="value">{companiesCount}</span>
          <span className="label">Companies</span>
        </div>
        <div className="divider"></div>
        <div className="stat-box">
          <span className="value">25 LPA</span>
          <span className="label">Highest Package</span>
        </div>
        <div className="divider"></div>
        <div className="stat-box">
          <span className="value">6-8 LPA</span>
          <span className="label">Average Package</span>
        </div>
      </motion.div>

      {/* Interactive Products / Events Cards */}
      <motion.div 
        className="C2wLanding_cards_grid"
        variants={cardsContainerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="product-card" onClick={onIncubatorsClick} variants={cardItemVariants}>
          <div className="card-top">
            <img
              src="/assets/images/landing/incubators.svg"
              alt="incubators"
              className="card-logo incubators-logo"
            />
            <button className="arrow-btn" aria-label="Go to Incubators">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 19L19 5M19 5H9M19 5V15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <p className="card-desc">Training And Internship Programs</p>
        </motion.div>

        <motion.div className="product-card" onClick={() => navigate('/superx')} variants={cardItemVariants}>
          <div className="card-top">
            <img
              src="/assets/images/landing/superx.svg"
              alt="SuperX"
              className="card-logo superx-logo"
            />
            <button className="arrow-btn" aria-label="Go to SuperX">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 19L19 5M19 5H9M19 5V15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <p className="card-desc">A Project Presentation Platform For You</p>
        </motion.div>

        <motion.div className="product-card" onClick={() => navigate('/quizeethon-event')} variants={cardItemVariants}>
          <div className="card-top">
            <img
              src="/assets/images/landing/quizeethon.svg"
              alt="Quizeethon"
              className="card-logo quizeethon-logo"
            />
            <button className="arrow-btn" aria-label="Go to Quizeethon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 19L19 5M19 5H9M19 5V15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <p className="card-desc">Quiz Competition & Quick Thinking.</p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default C2wLanding;
